import { directive, Directive } from 'lit/directive.js';
import { LitElement, css, html } from 'lit';
import { state, query, property, queryAssignedElements, customElement } from 'lit/decorators.js';
import { unsafeStatic, html as html$1 } from 'lit/static-html.js';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}

function __metadata(metadataKey, metadataValue) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(metadataKey, metadataValue);
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

const ARIA_IDREF_ATTRIBUTES = new Map([
    ['aria-activedescendant', 'ariaActivedescendantElement'],
    ['aria-controls', 'ariaControlsElements'],
    ['aria-describedby', 'ariaDescribedByElements'],
    ['aria-details', 'ariaDetailsElements'],
    ['aria-errormessage', 'ariaErrormessageElements'],
    ['aria-flowto', 'ariaFlowtoElements'],
    ['aria-labelledby', 'ariaLabelledByElements'],
    ['aria-owns', 'ariaOwnsElements'],
]);
function resolveAriaIdRefs(element, value) {
    const ids = value
        .trim()
        .split(/\s+/)
        .filter((id) => id.length > 0);
    if (ids.length === 0)
        return null;
    const root = element.getRootNode();
    let resolvedElements = [];
    if (root instanceof ShadowRoot) {
        resolvedElements = ids.map((id) => root.getElementById(id));
    }
    else if (root instanceof Document) {
        resolvedElements = ids.map((id) => root.getElementById(id));
    }
    else {
        resolvedElements = ids.map((id) => document.getElementById(id));
    }
    if (resolvedElements.filter((el) => el !== null).length === 0) {
        resolvedElements = ids.map((id) => document.getElementById(id));
    }
    const validElements = resolvedElements.filter((el) => el !== null);
    return validElements.length === ids.length ? validElements : null;
}
function setInternalsProperty(el, name, resolvedElements) {
    if (name === 'aria-activedescendant') {
        const [firstElement] = resolvedElements;
        // eslint-disable-next-line no-param-reassign
        el[name] = firstElement;
    }
    else {
        // eslint-disable-next-line no-param-reassign
        el[name] = resolvedElements;
    }
}
class SetAttributesDirective extends Directive {
    update(part, [attributes]) {
        const el = part.element;
        const internals = el.internals ?? null;
        for (const [name, value] of Object.entries(attributes)) {
            if (value == null) {
                el.removeAttribute(name);
            }
            else {
                const internalsPropertyName = ARIA_IDREF_ATTRIBUTES.get(name);
                if (internalsPropertyName) {
                    const resolvedElements = resolveAriaIdRefs(el, String(value));
                    if (resolvedElements) {
                        if (internals) {
                            // Set on ElementInternals
                            setInternalsProperty(internals, internalsPropertyName, resolvedElements);
                        }
                        else {
                            // Set internals property on shadowdom item
                            el.setAttribute(name, String(value));
                            setInternalsProperty(el, internalsPropertyName, resolvedElements);
                        }
                    }
                }
                else {
                    // Non-ARIA IDREF attributes: set normally
                    el.setAttribute(name, String(value));
                }
            }
        }
        return null;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    render(_attributes) {
        return null;
    }
}
/* How to use:

<textarea ${setAttributes(this._ariaAttributes)} ... />
*/
directive(SetAttributesDirective);

function r(e){var t,f,n="";if("string"==typeof e||"number"==typeof e)n+=e;else if("object"==typeof e)if(Array.isArray(e)){var o=e.length;for(t=0;t<o;t++)e[t]&&(f=r(e[t]))&&(n&&(n+=" "),n+=f);}else for(f in e)e[f]&&(n&&(n+=" "),n+=f);return n}function clsx(){for(var e,t,f=0,n="",o=arguments.length;f<o;f++)(e=arguments[f])&&(t=r(e))&&(n&&(n+=" "),n+=t);return n}

const connectedElements = new Set();
if (window && !window.translations) {
    window.translations = new Map();
}
const { translations } = window;
let fallback;
// TODO: We need some way for users to be able to set these on the server.
let documentDirection = 'ltr';
// Fallback for server.
let documentLanguage = 'en';
const isClient = typeof MutationObserver !== 'undefined' &&
    typeof document !== 'undefined' &&
    typeof document.documentElement !== 'undefined';
/** Updates all localized elements that are currently connected */
function update() {
    if (isClient) {
        documentDirection = document.documentElement.dir || 'ltr';
        documentLanguage = document.documentElement.lang || navigator.language;
    }
    [...connectedElements.keys()].forEach((el) => {
        const litEl = el;
        if (typeof litEl.requestUpdate === 'function') {
            litEl.requestUpdate();
        }
    });
}
if (isClient) {
    const documentElementObserver = new MutationObserver(update);
    documentDirection = document.documentElement.dir || 'ltr';
    documentLanguage = document.documentElement.lang || navigator.language;
    // Watch for changes on <html lang>
    documentElementObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['dir', 'lang'],
    });
}
/** Registers one or more translations */
function registerTranslation(...translation) {
    translation.forEach((t) => {
        const code = t.$code.toLowerCase();
        if (translations.has(code)) {
            // Merge translations that share the same language code
            translations.set(code, { ...translations.get(code), ...t });
        }
        else {
            translations.set(code, t);
        }
        // The first translation that's registered is the fallback
        if (!fallback) {
            fallback = t;
        }
    });
    update();
}
if (typeof window !== 'undefined') {
    window.registerTranslation = registerTranslation;
}
/**
 * Localize Reactive Controller for components built with Lit
 *
 * To use this controller, import the class and instantiate it in a custom element constructor:
 *
 * private localize = new LocalizeController(this);
 *
 * This will add the element to the set and make it respond to changes to <html dir|lang> automatically. To make it
 * respond to changes to its own dir|lang properties, make it a property:
 *
 *   @property() dir: string;
 *   @property() lang: string;
 *
 * To use a translation method, call it like this:
 *
 *   ${this.localize.term('term_key_here')}
 *   ${this.localize.date('2021-12-03')}
 *   ${this.localize.number(1000000)}
 */
class LocalizeController {
    constructor(host) {
        this.host = host;
        this.host.addController(this);
    }
    hostConnected() {
        connectedElements.add(this.host);
    }
    hostDisconnected() {
        connectedElements.delete(this.host);
    }
    /**
     * Gets the host element's directionality as determined by the `dir` attribute. The return value is transformed to
     * lowercase.
     */
    dir() {
        return `${this.host.dir || documentDirection}`.toLowerCase();
    }
    /**
     * Gets the host element's language as determined by the `lang` attribute. The return value is transformed to
     * lowercase.
     */
    lang() {
        return `${this.host.lang || documentLanguage}`.toLowerCase();
    }
    // eslint-disable-next-line class-methods-use-this
    getTranslationData(lang) {
        // Convert "en_US" to "en-US". Note that both underscores and dashes are allowed per spec, but underscores result in
        // a RangeError by the call to `new Intl.Locale()`. See: https://unicode.org/reports/tr35/#unicode-locale-identifier
        const locale = new Intl.Locale(lang.replace(/_/g, '-'));
        const language = locale?.language.toLowerCase();
        const region = locale?.region?.toLowerCase() ?? '';
        const primary = translations.get(`${language}-${region}`);
        const secondary = translations.get(language);
        return { locale, language, region, primary, secondary };
    }
    /** Determines if the specified term exists, optionally checking the fallback translation. */
    exists(key, options) {
        const { primary, secondary } = this.getTranslationData(options.lang ?? this.lang());
        const mergedOptions = {
            includeFallback: false,
            ...options,
        };
        if ((primary && primary[key]) ||
            (secondary && secondary[key]) ||
            (mergedOptions.includeFallback && fallback && fallback[key])) {
            return true;
        }
        return false;
    }
    /** Outputs a translated term. */
    term(key, ...args) {
        const { primary, secondary } = this.getTranslationData(this.lang());
        let term;
        // Look for a matching term using regionCode, code, then the fallback
        if (primary && primary[key]) {
            term = primary[key];
        }
        else if (secondary && secondary[key]) {
            term = secondary[key];
        }
        else if (fallback && fallback[key]) {
            term = fallback[key];
        }
        else {
            // eslint-disable-next-line no-console
            console.error(`No translation found for: ${String(key)}`);
            return String(key);
        }
        if (typeof term === 'function') {
            return term(...args);
        }
        return term;
    }
    /** Outputs a localized date in the specified format. */
    date(dateToFormat, options) {
        const date = new Date(dateToFormat);
        return new Intl.DateTimeFormat(this.lang(), options).format(date);
    }
    /** Outputs a localized number in the specified format. */
    number(numberToFormat, options) {
        const num = Number(numberToFormat);
        return Number.isNaN(num) ? '' : new Intl.NumberFormat(this.lang(), options).format(num);
    }
    /** Outputs a localized time in relative format. */
    relativeTime(value, unit, options) {
        return new Intl.RelativeTimeFormat(this.lang(), options).format(value, unit);
    }
}

/**
 * @param Base The base class.
 * @returns A mix-in implementing `localizations` method.
 *
 *@example
 *    <!-- Terms -->
      ${this.$localize.term('hello')}
      or
      ${this.$t('hello')}

      <!-- Dates -->
      ${this.$localize.date('2021-09-15 14:00:00 ET', { month: 'long', day: 'numeric', year: 'numeric' })}
      or
      ${this.$d('2021-09-15 14:00:00 ET', { month: 'long', day: 'numeric', year: 'numeric' })}

      <!-- Numbers/currency -->
      ${this.$localize.number(1000, { style: 'currency', currency: 'USD'})}
      or
      ${this.$n(1000,{ style: 'currency', currency: 'USD'})}

      <!-- Determining language -->
      ${this.$localize.lang()}

      <!-- Determining directionality, e.g. 'ltr' or 'rtl' -->
      ${this.$localize.dir()}


    *** HOW TO DEFINE TRANSLATIONS: ***
    // Simple terms
    upload: 'Upload',

    // Terms with placeholders
    greetUser: (name: string) => `Hello, ${name}!`,

    // Plurals
    numFilesSelected: (count: number) => {
        if (count === 0) return 'No files selected';
        if (count === 1) return '1 file selected';
        return `${count} files selected`;
    }
 */
const LocalizeMixin = (Base) => class extends Base {
    constructor() {
        super(...arguments);
        this.localize = new LocalizeController(this);
    }
    // Provide default values to avoid definite assignment errors and avoid decorators
    // commentati perchè danno problemi su React.js. Sono attributi nativi, e assegnare un valore di default a react da fastidio
    // dir: string = '';
    // lang: string = '';
    /**
     * Restituisce tutta l'utility di traduzione
     *

     *
     * @returns tutta l'utility di traduzione
     *
     * @example
     * this.$localize.lang() -> ritorna la lingua corrente
     * this.$localize.dir() -> ritorna la direzione della lingua corrente
     */
    get $localize() {
        return this.localize;
    }
    /**
     * Restituisce una stringa localizzata a partire da una chiave di termine.
     *
     * Utilizza il `LocalizeController` per accedere al dizionario corrente e
     * tradurre la chiave fornita secondo la lingua attiva.
     *
     * @param t - La chiave del termine da localizzare (es. 'hello', 'submit', ecc.).
     * @returns La stringa tradotta in base alla lingua attiva. Se la chiave non è trovata, restituisce la chiave stessa.
     *
     * @example
     * this.$t('hello'); // → "Ciao" (in locale it-IT)
     */
    $t(t) {
        // format term
        return this.localize.term(t);
    }
    /**
     * Formatta una data in base alla localizzazione attiva.
     *
     * Utilizza il `LocalizeController` per restituire una stringa localizzata
     * secondo le opzioni fornite (es. mese esteso, anno, ecc.).
     *
     * @param n - La data da formattare come stringa compatibile (es. ISO o con timezone, es. '2021-09-15 14:00:00 ET').
     * @param p - Le opzioni di formattazione per `Intl.DateTimeFormat` (es. { year: 'numeric', month: 'long', day: 'numeric' }).
     * @returns Una stringa rappresentante la data formattata secondo la localizzazione attiva.
     *
     * @example
     * this.$d('2021-09-15 14:00:00 ET', { year: 'numeric', month: 'long', day: 'numeric' });
     * // → "15 settembre 2021" (in locale it-IT)
     */
    $d(d, p) {
        // format date
        return this.localize.date(d, p);
    }
    /**
     * Formatta un numero secondo le impostazioni locali dell'utente corrente.
     *
     * Utilizza il `LocalizeController` per applicare formattazione numerica,
     * incluse opzioni come separatori, decimali, valute, ecc.
     *
     * @param d - Il numero da formattare.
     * @param p - Le opzioni di formattazione (es. { style: 'currency', currency: 'EUR' }).
     * @returns Una stringa rappresentante il numero formattato secondo la localizzazione attiva.
     *
     * @example
     * this.$n(1234.56, { style: 'currency', currency: 'USD' }); // → "$1,234.56" (in locale en-US)
     */
    $n(d, p) {
        return this.localize.number(d, p);
    }
};

/* eslint-disable no-console */
class Logger {
    constructor(tag) {
        this.tag = tag;
    }
    format(level, msg) {
        return [`[${this.tag}] ${msg}`];
    }
    warn(msg) {
        console.warn(...this.format('warn', msg));
    }
    error(msg) {
        console.error(...this.format('error', msg));
    }
    info(msg) {
        console.info(...this.format('info', msg));
    }
}

/**
 * Factory function per creare una base class estendibile
 * con stili personalizzati.
 */
class BaseComponent extends LitElement {
    constructor() {
        super();
        this.composeClass = clsx;
        this.logger = new Logger(this.tagName.toLowerCase());
    }
    get _ariaAttributes() {
        const attributes = {};
        for (const attr of this.getAttributeNames()) {
            if (attr === 'it-role') {
                attributes.role = this.getAttribute(attr);
            }
            else if (attr.startsWith('it-aria-')) {
                attributes[attr.replace(/^it-/, '')] = this.getAttribute(attr);
            }
        }
        return attributes;
    }
    // eslint-disable-next-line class-methods-use-this
    generateId(prefix) {
        return `${prefix}-${Math.random().toString(36).slice(2)}`;
    }
    // eslint-disable-next-line class-methods-use-this, @typescript-eslint/no-unused-vars
    addFocus(element) {
        // new TrackFocus(element); // per il momento è stato disattivato perchè ci sono le pseudo classi ::focus-visible per fare quello che fa TrackFocus. Si possono aggiungere regole css in bsi-italia 3 dato che stiamo facendo una breaking release di bsi.
    }
    // eslint-disable-next-line class-methods-use-this
    getActiveElement() {
        let active = document.activeElement;
        while (active && active.shadowRoot && active.shadowRoot.activeElement) {
            active = active.shadowRoot.activeElement;
        }
        return active;
    }
    get focusElement() {
        return this;
    }
    // eslint-disable-next-line class-methods-use-this
    get prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    connectedCallback() {
        super.connectedCallback();
        // generate internal _id
        const prefix = this.id?.length > 0 ? this.id : this.tagName.toLowerCase();
        this._id = this.generateId(prefix);
        this.ariaAttributesObserver = new MutationObserver((mutations) => {
            const shouldUpdate = mutations.some((mutation) => {
                if (mutation.type !== 'attributes' || !mutation.attributeName) {
                    return false;
                }
                return mutation.attributeName === 'it-role' || mutation.attributeName.startsWith('it-aria-');
            });
            if (shouldUpdate) {
                this.requestUpdate();
            }
        });
        this.ariaAttributesObserver.observe(this, {
            attributes: true,
            attributeOldValue: true,
        });
    }
    disconnectedCallback() {
        this.ariaAttributesObserver?.disconnect();
        this.ariaAttributesObserver = undefined;
        super.disconnectedCallback();
    }
}
const BaseLocalizedComponent = LocalizeMixin(BaseComponent);

//
// We store a WeakMap of forms + controls so we can keep references to all FormControl within a given form. As
// elements connect and disconnect to/from the DOM, their containing form is used as the key and the form control is
// added and removed from the form's set, respectively.
//
const formCollections = new WeakMap();
//
// We store a WeakMap of reportValidity() overloads so we can override it when form controls connect to the DOM and
// restore the original behavior when they disconnect.
//
const reportValidityOverloads = new WeakMap();
const checkValidityOverloads = new WeakMap();
//
// We store a Set of controls that users have interacted with. This allows us to determine the interaction state
// without littering the DOM with additional data attributes.
//
const userInteractedControls = new WeakSet();
//
// We store a WeakMap of interactions for each form control so we can track when all conditions are met for validation.
//
const interactions = new WeakMap();
/** A reactive controller to allow form controls to participate in form submission, validation, etc. */
class FormControlController {
    constructor(host, options) {
        this.submittedOnce = false;
        this.handleFormData = (event) => {
            // console.log('handleFormData');
            const disabled = this.options.disabled(this.host);
            const name = this.options.name(this.host);
            const value = this.options.value(this.host);
            const tagName = this.host.tagName.toLowerCase();
            // For buttons, we only submit the value if they were the submitter. This is currently done in doAction() by
            // injecting the name/value on a temporary button, so we can just skip them here.
            const isButton = tagName === 'it-button';
            if (this.host.isConnected &&
                !disabled &&
                !isButton &&
                typeof name === 'string' &&
                name.length > 0 &&
                typeof value !== 'undefined') {
                switch (tagName) {
                    case 'it-radio':
                        if (this.host.checked) {
                            event.formData.append(name, value);
                        }
                        break;
                    case 'it-checkbox':
                    case 'it-toggle':
                        if (this.host.checked) {
                            if (event.formData.getAll(name).indexOf(value) < 0) {
                                // handle group checkbox
                                event.formData.append(name, value);
                            }
                        }
                        break;
                    case 'it-checkbox-group':
                    case 'it-toggle-group':
                        // non settare valori in formData, perchè ogni singola checkbox setta il suo valore
                        break;
                    case 'it-upload':
                        // value is File[] — append each File object directly (not as string)
                        if (Array.isArray(value)) {
                            value.forEach((file) => {
                                event.formData.append(name, file);
                            });
                        }
                        break;
                    default:
                        if (Array.isArray(value)) {
                            value.forEach((val) => {
                                event.formData.append(name, val.toString());
                            });
                        }
                        else {
                            event.formData.append(name, value.toString());
                        }
                }
            }
        };
        this.handleFormSubmit = (event) => {
            const disabled = this.options.disabled(this.host);
            const reportValidity = this.options.reportValidity;
            // Update the interacted state for all controls when the form is submitted
            if (this.form && !this.form.noValidate) {
                formCollections.get(this.form)?.forEach((control) => {
                    this.setUserInteracted(control, true);
                });
            }
            const resReportValidity = reportValidity(this.host);
            if (this.form && !this.form.noValidate && !disabled && !resReportValidity) {
                event.preventDefault();
                // event.stopImmediatePropagation(); // se lo attiviamo, valida un campo alla volta
                // Scroll al primo controllo non valido
                const formControls = formCollections.get(this.form);
                if (formControls) {
                    for (const control of formControls) {
                        if (!control.validity?.valid) {
                            // Scroll smooth verso il controllo non valido
                            control.scrollIntoView({
                                behavior: 'smooth',
                                block: 'center',
                            });
                            break;
                        }
                    }
                }
            }
            this.submittedOnce = true;
        };
        this.handleFormReset = () => {
            this.options.setValue(this.host, '');
            this.submittedOnce = false;
            this.setUserInteracted(this.host, false);
            interactions.set(this.host, []);
        };
        this.handleInteraction = (event) => {
            const emittedEvents = interactions.get(this.host);
            if (!emittedEvents.includes(event.type)) {
                emittedEvents.push(event.type);
            }
            // Mark it as user-interacted as soon as all associated events have been emitted
            if (emittedEvents.length === this.options.assumeInteractionOn.length) {
                this.setUserInteracted(this.host, true);
            }
        };
        this.checkFormValidity = () => {
            // console.log('checkFormValidity');
            //
            // This is very similar to the `reportFormValidity` function, but it does not trigger native constraint validation
            // Allow the user to simply check if the form is valid and handling validity in their own way.
            //
            // We preserve the original method in a WeakMap, but we don't call it from the overload because that would trigger
            // validations in an unexpected order. When the element disconnects, we revert to the original behavior. This won't
            // be necessary once we can use ElementInternals.
            //
            // Note that we're also honoring the form's novalidate attribute.
            //
            if (this.form && !this.form.noValidate) {
                // This seems sloppy, but checking all elements will cover native inputs, Shoelace inputs, and other custom
                // elements that support the constraint validation API.
                const elements = this.form.querySelectorAll('*');
                for (const element of elements) {
                    if (typeof element.checkValidity === 'function') {
                        if (!element.checkValidity()) {
                            return false;
                        }
                    }
                }
            }
            return true;
        };
        this.reportFormValidity = () => {
            // console.log('reportFormValidity');
            //
            // FormControl work hard to act like regular form controls. They support the Constraint Validation API
            // and its associated methods such as setCustomValidity() and reportValidity(). However, the HTMLFormElement also
            // has a reportValidity() method that will trigger validation on all child controls. Since we're not yet using
            // ElementInternals, we need to overload this method so it looks for any element with the reportValidity() method.
            //
            // We preserve the original method in a WeakMap, but we don't call it from the overload because that would trigger
            // validations in an unexpected order. When the element disconnects, we revert to the original behavior. This won't
            // be necessary once we can use ElementInternals.
            //
            // Note that we're also honoring the form's novalidate attribute.
            //
            if (this.form && !this.form.noValidate) {
                // This seems sloppy, but checking all elements will cover native inputs, Shoelace inputs, and other custom
                // elements that support the constraint validation API.
                const elements = this.form.querySelectorAll('*');
                for (const element of elements) {
                    if (typeof element.reportValidity === 'function') {
                        if (!element.reportValidity()) {
                            return false;
                        }
                    }
                }
            }
            return true;
        };
        (this.host = host).addController(this);
        this.options = {
            form: (input) => {
                // If there's a form attribute, use it to find the target form by id
                // Controls may not always reflect the 'form' property. For example, `<it-button>` doesn't reflect.
                const formId = input.form;
                if (formId) {
                    const root = input.getRootNode();
                    const form = root.querySelector(`#${formId}`);
                    if (form) {
                        return form;
                    }
                }
                return input.closest('form');
            },
            name: (input) => input.name,
            value: (input) => input.value,
            disabled: (input) => input.disabled ?? false,
            reportValidity: (input) => typeof input.reportValidity === 'function' ? input.reportValidity() : true,
            checkValidity: (input) => (typeof input.checkValidity === 'function' ? input.checkValidity() : true),
            setValue: (input, value) => {
                // eslint-disable-next-line no-param-reassign
                input.value = value;
            },
            assumeInteractionOn: ['it-input'],
            ...options,
        };
    }
    hostConnected() {
        const form = this.options.form(this.host);
        if (form) {
            this.attachForm(form);
        }
        // Listen for interactions
        interactions.set(this.host, []);
        this.options.assumeInteractionOn.forEach((event) => {
            this.host.addEventListener(event, this.handleInteraction);
        });
    }
    hostDisconnected() {
        this.detachForm();
        // Clean up interactions
        interactions.delete(this.host);
        this.options.assumeInteractionOn.forEach((event) => {
            this.host.removeEventListener(event, this.handleInteraction);
        });
    }
    hostUpdated() {
        const form = this.options.form(this.host);
        // Detach if the form no longer exists
        if (!form) {
            this.detachForm();
        }
        // If the form has changed, reattach it
        if (form && this.form !== form) {
            this.detachForm();
            this.attachForm(form);
        }
        if (this.host.hasUpdated) {
            this.setValidity(this.host.validity.valid);
        }
    }
    attachForm(form) {
        if (form) {
            this.form = form;
            // Add this element to the form's collection
            if (formCollections.has(this.form)) {
                formCollections.get(this.form).add(this.host);
            }
            else {
                formCollections.set(this.form, new Set([this.host]));
            }
            this.form.addEventListener('formdata', this.handleFormData);
            this.form.addEventListener('submit', this.handleFormSubmit);
            this.form.addEventListener('reset', this.handleFormReset);
            // Overload the form's reportValidity() method so it looks at FormControl
            if (!reportValidityOverloads.has(this.form)) {
                reportValidityOverloads.set(this.form, this.form.reportValidity);
                this.form.reportValidity = () => this.reportFormValidity();
            }
            // Overload the form's checkValidity() method so it looks at FormControl
            if (!checkValidityOverloads.has(this.form)) {
                checkValidityOverloads.set(this.form, this.form.checkValidity);
                this.form.checkValidity = () => this.checkFormValidity();
            }
        }
        else {
            this.form = undefined;
        }
    }
    detachForm() {
        if (!this.form)
            return;
        const formCollection = formCollections.get(this.form);
        if (!formCollection) {
            return;
        }
        this.submittedOnce = false;
        // Remove this host from the form's collection
        formCollection.delete(this.host);
        // Check to make sure there's no other form controls in the collection. If we do this
        // without checking if any other controls are still in the collection, then we will wipe out the
        // validity checks for all other elements.
        // see: https://github.com/shoelace-style/shoelace/issues/1703
        if (formCollection.size <= 0) {
            this.form.removeEventListener('formdata', this.handleFormData);
            this.form.removeEventListener('submit', this.handleFormSubmit);
            this.form.removeEventListener('reset', this.handleFormReset);
            // Remove the overload and restore the original method
            if (reportValidityOverloads.has(this.form)) {
                this.form.reportValidity = reportValidityOverloads.get(this.form);
                reportValidityOverloads.delete(this.form);
            }
            if (checkValidityOverloads.has(this.form)) {
                this.form.checkValidity = checkValidityOverloads.get(this.form);
                checkValidityOverloads.delete(this.form);
            }
            // So it looks weird here to not always set the form to undefined. But I _think_ if we unattach this.form here,
            // we end up in this fun spot where future validity checks don't have a reference to the form validity handler.
            // First form element in sets the validity handler. So we can't clean up `this.form` until there are no other form elements in the form.
            this.form = undefined;
        }
    }
    // eslint-disable-next-line class-methods-use-this
    setUserInteracted(el, hasInteracted) {
        if (hasInteracted) {
            userInteractedControls.add(el);
        }
        else {
            userInteractedControls.delete(el);
        }
        el.requestUpdate();
    }
    doAction(type, submitter) {
        // console.log('doaction', type);
        if (this.form) {
            const button = document.createElement('button');
            button.type = type;
            button.style.position = 'absolute';
            button.style.width = '0';
            button.style.height = '0';
            button.style.clipPath = 'inset(50%)';
            button.style.overflow = 'hidden';
            button.style.whiteSpace = 'nowrap';
            // Pass name, value, and form attributes through to the temporary button
            if (submitter) {
                button.name = submitter.name;
                button.value = submitter.value;
                ['formaction', 'formenctype', 'formmethod', 'formnovalidate', 'formtarget'].forEach((attr) => {
                    if (submitter.hasAttribute(attr)) {
                        button.setAttribute(attr, submitter.getAttribute(attr));
                    }
                });
            }
            this.form.append(button);
            button.click();
            button.remove();
        }
    }
    /** Returns the associated `<form>` element, if one exists. */
    getForm() {
        return this.form ?? null;
    }
    /** Resets the form, restoring all the control to their default value */
    reset(submitter) {
        this.doAction('reset', submitter);
    }
    /** Submits the form, triggering validation and form data injection. */
    submit(submitter) {
        // Calling form.submit() bypasses the submit event and constraint validation. To prevent this, we can inject a
        // native submit button into the form, "click" it, then remove it to simulate a standard form submission.
        this.doAction('submit', submitter);
    }
    /**
     * Synchronously sets the form control's validity. Call this when you know the future validity but need to update
     * the host element immediately, i.e. before Lit updates the component in the next update.
     */
    setValidity(isValid) {
        const host = this.host;
        const hasInteracted = Boolean(userInteractedControls.has(host));
        const required = Boolean(host.required);
        //
        // We're mapping the following "states" to data attributes. In the future, we can use ElementInternals.states to
        // create a similar mapping, but instead of [data-invalid] it will look like :--invalid.
        //
        // See this RFC for more details: https://github.com/shoelace-style/shoelace/issues/1011
        //
        host.toggleAttribute('data-required', required);
        host.toggleAttribute('data-optional', !required);
        host.toggleAttribute('data-invalid', !isValid);
        host.toggleAttribute('data-valid', isValid);
        host.toggleAttribute('data-user-invalid', !isValid && hasInteracted);
        host.toggleAttribute('data-user-valid', isValid && hasInteracted);
    }
    userInteracted() {
        const hasInteracted = Boolean(userInteractedControls.has(this.host));
        return hasInteracted;
    }
    /**
     * Updates the form control's validity based on the current value of `host.validity.valid`. Call this when anything
     * that affects constraint validation changes so the component receives the correct validity states.
     */
    updateValidity() {
        const host = this.host;
        this.setValidity(host.validity.valid);
    }
    /**
     * Dispatches a non-bubbling, cancelable custom event of type `it-invalid`.
     * If the `it-invalid` event will be cancelled then the original `invalid`
     * event (which may have been passed as argument) will also be cancelled.
     * If no original `invalid` event has been passed then the `it-invalid`
     * event will be cancelled before being dispatched.
     */
    emitInvalidEvent(originalInvalidEvent) {
        const itInvalidEvent = new CustomEvent('it-invalid', {
            bubbles: false,
            composed: false,
            cancelable: true,
            detail: {},
        });
        if (!originalInvalidEvent) {
            itInvalidEvent.preventDefault();
        }
        if (!this.host.dispatchEvent(itInvalidEvent)) {
            originalInvalidEvent?.preventDefault();
        }
    }
}

const translation = {
    $code: 'it',
    $name: 'Italiano',
    $dir: 'ltr',
    validityRequired: 'Questo campo è obbligatorio.',
    validityGroupRequired: "Scegli almeno un'opzione",
    validityPattern: 'Il valore non corrisponde al formato richiesto.',
    validityMinlength: 'Il valore deve essere lungo almeno {minlength} caratteri.',
    validityMaxlength: 'Il valore deve essere lungo al massimo {maxlength} caratteri.',
};

registerTranslation(translation);
class FormControl extends BaseLocalizedComponent {
    constructor() {
        super(...arguments);
        this.formControlController = new FormControlController(this, {
            assumeInteractionOn: ['it-input', 'it-blur', 'it-change'],
        });
        // TODO: verificare se serve davvero con il fatto che usiamo form-controller
        // static formAssociated = true;
        // @property()
        // internals = this.attachInternals();
        this._touched = false;
        /** The name of the input, submitted as a name/value pair with form data. */
        this.name = '';
        /** The current value of the input, submitted as a name/value pair with form data. */
        this.value = '';
        /** If the input is disabled. */
        this.disabled = false;
        /**
         * By default, form controls are associated with the nearest containing `<form>` element. This attribute allows you
         * to place the form control outside of a form and associate it with the form that has this `id`. The form must be in
         * the same document or shadow root for this to work.
         */
        this.form = '';
        /** If you implement your custom validation and you won't to trigger default validation */
        this.customValidation = false;
        /** If your input is invalid from your custom validition, set this attribute with message validation */
        this.validationText = '';
        /**
         * Specifies the granularity that the value must adhere to, or the special value `any` which means no stepping is
         * implied, allowing any numeric value. Only applies to date and number input types.
         */
        this.step = 'any';
        /** The input's minimum length. */
        this.minlength = -1;
        /** The input's maximum length. */
        this.maxlength = -1;
        /** If the input is required. */
        this.required = false;
        /* For grouped input, like checkbox-group */
        this.isInGroup = false;
        this.validationMessage = '';
    }
    /** Gets the validity state object */
    get validity() {
        return this.inputElement?.validity;
    }
    /** Gets the associated form, if one exists. */
    getForm() {
        return this.formControlController.getForm();
    }
    // Form validation methods
    checkValidity() {
        const inputValid = this.inputElement?.checkValidity() ?? true; // this.inputElement.checkValidity() è la validazione del browser
        return inputValid;
    }
    /** Checks for validity and shows the browser's validation message if the control is invalid. */
    reportValidity() {
        // const ret = this.inputElement.checkValidity();
        const ret = this.checkValidity(); // chiama la checkValidity, che se è stata overridata chiama quella
        this.handleValidationMessages();
        return ret; // this.inputElement.reportValidity();
    }
    /** Sets a custom validation message. Pass an empty string to restore validity. */
    setCustomValidity(message) {
        this.inputElement.setCustomValidity(message);
        this.validationMessage = this.inputElement.validationMessage;
        this.formControlController.updateValidity();
    }
    // Handlers
    _handleReady() {
        requestAnimationFrame(() => {
            this.dispatchEvent(new CustomEvent('it-input-ready', { bubbles: true, detail: { el: this.inputElement } }));
        });
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleInput(e) {
        this.handleValidationMessages();
        this.dispatchEvent(new CustomEvent('it-input', {
            detail: { value: this.inputElement.value, el: this.inputElement },
            bubbles: true,
            composed: true,
        }));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleBlur(e) {
        this._touched = true;
        this.handleValidationMessages();
        this.dispatchEvent(new FocusEvent('it-blur', { bubbles: true, composed: true }));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleFocus(e) {
        this.dispatchEvent(new FocusEvent('it-focus', { bubbles: true, composed: true }));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleClick(e) {
        this.dispatchEvent(new MouseEvent('it-click', { bubbles: true, composed: true }));
    }
    /*
    Override default browser validation messages
     */
    handleValidationMessages() {
        if (!this.customValidation) {
            const _v = this.inputElement.validity;
            const isRequiredHandledByGroup = this.isInGroup === true;
            if (_v.valueMissing && !isRequiredHandledByGroup) {
                this.setCustomValidity(this.$t('validityRequired'));
            }
            else if (_v.patternMismatch) {
                this.setCustomValidity(this.$t('validityPattern'));
            }
            else if (_v.tooShort) {
                this.setCustomValidity(this.$t('validityMinlength').replace('{minlength}', this.minlength.toString()));
            }
            else if (_v.tooLong) {
                this.setCustomValidity(this.$t('validityMaxlength').replace('{maxlength}', this.maxlength.toString()));
            }
            else {
                /* nothing. Usa il messaggio di errore della validazione
                di default del browser per altri errori di validità come:
                - typeMismatch
                - rangeUnderflow
                - rangeOverflow
                - stepMismatch
                - badInput */
                const otherConstraintErrors = _v.typeMismatch || _v.rangeUnderflow || _v.rangeOverflow || _v.stepMismatch || _v.badInput;
                if (!otherConstraintErrors) {
                    this.setCustomValidity('');
                }
            }
        }
        this.validationMessage = this.inputElement.validationMessage;
    }
    _handleInvalid(event) {
        this.formControlController.setValidity(false);
        this.formControlController.emitInvalidEvent(event);
    }
    _handleChange(e) {
        const target = e.target;
        let value;
        if (target instanceof HTMLInputElement) {
            switch (target.type) {
                case 'checkbox':
                case 'radio':
                    value = target.checked;
                    break;
                case 'file':
                    value = target.files; // FileList
                    break;
                default:
                    value = target.value;
            }
        }
        else if (target instanceof HTMLSelectElement) {
            if (target.multiple) {
                value = Array.from(target.selectedOptions).map((o) => o.value);
            }
            else {
                value = target.value;
            }
        }
        else {
            // textarea o altri input con value
            value = target.value;
        }
        this.dispatchEvent(new CustomEvent('it-change', {
            detail: { value, el: target },
            bubbles: true,
            composed: true,
        }));
    }
    updated(changedProperties) {
        super.updated?.(changedProperties);
        if (this.customValidation) {
            this.setCustomValidity(this.validationText ?? '');
        }
        else if (this.formControlController.userInteracted()) {
            this.formControlController.updateValidity();
        }
    }
}
__decorate([
    state(),
    __metadata("design:type", Object)
], FormControl.prototype, "_touched", void 0);
__decorate([
    query('.it-form__control'),
    __metadata("design:type", HTMLInputElement)
], FormControl.prototype, "inputElement", void 0);
__decorate([
    property({ type: String, reflect: true }) // from FormControl
    ,
    __metadata("design:type", Object)
], FormControl.prototype, "name", void 0);
__decorate([
    property({ reflect: true }),
    __metadata("design:type", Object)
], FormControl.prototype, "value", void 0);
__decorate([
    property({ type: Boolean, reflect: true }) // from FormControl
    ,
    __metadata("design:type", Object)
], FormControl.prototype, "disabled", void 0);
__decorate([
    property({ reflect: true, type: String }),
    __metadata("design:type", Object)
], FormControl.prototype, "form", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'custom-validation' }),
    __metadata("design:type", Object)
], FormControl.prototype, "customValidation", void 0);
__decorate([
    property({ attribute: 'validity-message', reflect: true }),
    __metadata("design:type", String)
], FormControl.prototype, "validationText", void 0);
__decorate([
    property(),
    __metadata("design:type", String)
], FormControl.prototype, "pattern", void 0);
__decorate([
    property(),
    __metadata("design:type", Object)
], FormControl.prototype, "min", void 0);
__decorate([
    property(),
    __metadata("design:type", Object)
], FormControl.prototype, "max", void 0);
__decorate([
    property(),
    __metadata("design:type", Object)
], FormControl.prototype, "step", void 0);
__decorate([
    property({ type: Number }),
    __metadata("design:type", Object)
], FormControl.prototype, "minlength", void 0);
__decorate([
    property({ type: Number }),
    __metadata("design:type", Object)
], FormControl.prototype, "maxlength", void 0);
__decorate([
    property({ type: Boolean, reflect: true }) // from FormControl
    ,
    __metadata("design:type", Object)
], FormControl.prototype, "required", void 0);
__decorate([
    property({ type: Boolean }),
    __metadata("design:type", Object)
], FormControl.prototype, "isInGroup", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], FormControl.prototype, "validationMessage", void 0);

if (typeof window !== 'undefined') {
    window._itAnalytics = window._itAnalytics || {};
    window._itAnalytics = {
        ...window._itAnalytics,
        version: '1.0.0-alpha.19',
    };
}

const CARD_VARIANTS = [
    'default',
    'inline',
    'inline-reverse',
    'inline-mini',
    'inline-mini-reverse',
    'profile',
    'location',
    'banner',
    'inline-banner',
    'inline-banner-reverse',
    'inline-banner-mini',
    'inline-banner-mini-reverse',
];
const CARD_BORDER_COLORS = ['primary', 'secondary', 'success', 'danger', 'warning'];
const CARD_IMAGE_RATIOS = ['1x1', '4x3', '16x9', '21x9'];
const CARD_SHADOWS = ['none', 'sm', 'md', 'lg'];
const CARD_HEADING_LEVELS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];
const CARD_HEADING_SIZES = ['sm', 'md'];

var styles = css`@charset "UTF-8";
.ratio {
  position: relative;
  width: 100%;
}
.ratio::before {
  display: block;
  padding-top: var(--bsi-aspect-ratio);
  content: "";
}
.ratio > * {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.ratio-1x1 {
  --bsi-aspect-ratio: 100%;
}

.ratio-3x2 {
  --bsi-aspect-ratio: 66.6666666667%;
}

.ratio-4x3 {
  --bsi-aspect-ratio: 75%;
}

.ratio-16x9 {
  --bsi-aspect-ratio: 56.25%;
}

.ratio-21x9 {
  --bsi-aspect-ratio: 42.8571428571%;
}

.it-card {
  --bsi-card-background: var(--bsi-color-background-inverse);
  --bsi-card-border-color: var(--bsi-color-border-subtle);
  --bsi-card-border-radius: var(--bsi-radius-smooth);
  --bsi-card-border-radius-inner: calc(
    var(--bsi-card-border-radius) - var(--bsi-card-border-outside-width)
  );
  --bsi-card-border-top-color: transparent;
  --bsi-card-border-top-width: 6px;
  --bsi-card-border-outside-width: 1px;
  --bsi-card-border-separator-width: 1px;
  --bsi-card-footer-background: transparent;
  --bsi-card-footer-text-color: var(--bsi-color-text-muted);
  --bsi-card-spacing-x: var(--bsi-spacing-s);
  --bsi-card-spacing-y: var(--bsi-spacing-s);
  --bsi-card-category-color: var(--bsi-color-text-muted);
  --bsi-card-category-font-weight: var(--bsi-font-weight-solid);
  --bsi-card-category-font-size: var(--bsi-label-font-size);
  --bsi-card-text-color: var(--bsi-color-text-secondary);
  --bsi-card-date-color: var(--bsi-color-text-muted);
  --bsi-card-date-font-size: var(--bsi-label-font-size-s);
  --bsi-card-description-color: var(--bsi-color-text-muted);
  --bsi-card-height: auto;
  --bsi-card-inline-min-height: 240px;
  --bsi-card-inline-mini-min-height: 160px;
  --bsi-card-link-color: var(--bsi-color-text-secondary);
  --bsi-card-title-spacing-y: var(
    --bsi-card-spacing-y
  );
  --bsi-card-text-font-size: var(--bsi-font-size-3);
  --bsi-card-signature-font-size: var(--bsi-label-font-size);
  --bsi-card-signature-font-family: var(--bsi-font-mono);
}

/* stylelint-disable-next-line no-duplicate-selectors */
.it-card {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  height: var(--bsi-card-height);
  padding: var(--bsi-card-spacing-y) var(--bsi-card-spacing-x);
  background-color: var(--bsi-card-background);
  color: var(--bsi-card-text-color);
  word-break: break-word;
  overflow-wrap: break-word;
  background-clip: border-box;
}
.it-card .it-card-title {
  margin-bottom: var(--bsi-spacing-xxs);
}
.it-card .it-card-title.it-card-title-icon {
  display: flex;
  justify-content: space-between;
}
.it-card .it-card-title.it-card-title-icon > * {
  margin: 0.25rem;
}
@supports (gap: 0.5rem) {
  .it-card .it-card-title.it-card-title-icon {
    gap: 0.5rem;
  }
  .it-card .it-card-title.it-card-title-icon > * {
    margin: 0;
  }
}
.it-card .it-card-title.it-card-title-icon .it-card-title-icon-wrapper {
  margin-left: var(--bsi-icon-spacing);
}
.it-card .it-card-title.it-card-title-icon > a {
  display: flex;
  justify-content: space-between;
  width: 100%;
}
.it-card .it-card-footer {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: flex-end;
  margin-top: var(--bsi-card-spacing-y);
  gap: var(--bsi-card-spacing-y);
  color: var(--bsi-card-footer-text-color);
}
.it-card .it-card-taxonomy {
  flex-grow: 1;
}
.it-card .it-card-category {
  display: inline-block;
  margin-bottom: 0;
  color: var(--bsi-card-category-color);
  font-size: var(--bsi-card-category-font-size);
  font-weight: var(--bsi-card-category-font-weight);
  text-transform: uppercase;
}
.it-card .it-card-chips {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 0;
  padding: 0;
  list-style-type: none;
  gap: calc(var(--bsi-card-spacing-y) * 0.5);
}
.it-card .it-card-date {
  color: var(--bsi-card-date-color);
  font-size: var(--bsi-card-date-font-size);
}
.it-card .it-card-description-list {
  display: flex;
  flex-direction: column;
  padding-top: var(--bsi-card-spacing-y);
  padding-bottom: var(--bsi-card-spacing-y);
  border-top: var(--bsi-card-border-separator-width) solid var(--bsi-card-border-color);
  border-bottom: var(--bsi-card-border-separator-width) solid var(--bsi-card-border-color);
  color: var(--bsi-card-description-color);
  font-size: var(--bsi-label-font-size);
  line-height: var(--bsi-label-leading);
  gap: var(--bsi-spacing-s);
}
@media (max-width: 575.98px) {
  .it-card .it-card-description-list {
    display: flex;
    flex-direction: column;
  }
}
.it-card .it-card-description-list:last-child {
  margin-bottom: 0;
  border-bottom: none;
}
.it-card .it-card-description-list dt {
  display: inline;
  margin-right: 0.125rem;
  font-weight: bold;
}
.it-card .it-card-description-list dd {
  display: inline-block;
  margin: 0;
}
.it-card .it-card-actions {
  margin-top: var(--bsi-card-spacing-y);
  padding-top: var(--bsi-card-spacing-y);
  border-top: var(--bsi-card-border-separator-width) solid var(--bsi-card-border-color);
  background-color: var(--bsi-card-footer-background);
  font-size: var(--bsi-label-font-size);
  line-height: var(--bsi-label-leading);
}
.it-card .it-card-actions:last-child {
  border-radius: 0 0 var(--bsi-card-border-radius-inner) var(--bsi-card-border-radius-inner);
}
.it-card .it-card-link {
  color: var(--bsi-card-link-color);
}
.it-card .it-card-link:hover {
  color: color-hover(hsl(210, 33%, 28%));
}
.it-card .it-card-link + .it-card-link {
  margin-left: var(--bsi-card-spacing-x);
}
.it-card .it-card-body {
  flex: 1 1 auto;
  color: var(--bsi-card-text-color);
}
.it-card .it-card-body .it-card-subtitle {
  margin-bottom: var(--bsi-spacing-xxs);
  font-size: var(--bsi-heading-5-font-size);
  font-weight: var(--bsi-font-weight-solid);
  line-height: var(--bsi-font-leading-2);
}
.it-card .it-card-body .it-card-text {
  font-size: var(--bsi-card-text-font-size);
  line-height: var(--bsi-body-leading);
}
.it-card .it-card-body .it-card-text:last-child {
  margin-bottom: 0;
}
.it-card .it-card-body .it-card-signature {
  margin-bottom: calc(0.5 * var(--bsi-card-title-spacing-y));
  color: var(--bsi-card-text-color);
  font-family: var(--bsi-card-signature-font-family);
  font-size: var(--bsi-card-signature-font-size);
}
.it-card.it-card-image {
  padding-top: 0;
}
.it-card.it-card-image.it-card-inline, .it-card.it-card-image.it-card-inline-reverse {
  padding-bottom: 0;
}
.it-card.it-card-image.it-card-inline {
  padding-left: 0;
  padding-right: 0;
}
.it-card.it-card-height-full {
  height: 100%;
}
.it-card.it-card-profile .it-card-profile-header {
  display: flex;
  flex-wrap: wrap-reverse;
  align-items: center;
  justify-content: space-between;
  gap: var(--bsi-card-spacing-x);
}
.it-card.it-card-profile .it-card-profile-header .it-card-profile-content .it-card-profile-name {
  margin-bottom: var(--bsi-spacing-3xs);
}
.it-card.it-card-profile .it-card-profile-header .it-card-profile-content .it-card-profile-role,
.it-card.it-card-profile .it-card-profile-header .it-card-profile-content .it-card-profile-type {
  margin-bottom: 0;
  font-size: var(--bsi-label-font-size);
  font-weight: var(--bsi-font-weight-solid);
  line-height: var(--bsi-label-leading);
}
.it-card.it-card-profile .it-card-profile-header .it-card-profile-image {
  max-width: 80px;
  max-height: 80px;
}
.it-card.it-card-profile .it-card-profile-header .it-card-profile-image figure {
  margin-bottom: 0;
}
.it-card.it-card-profile .it-card-profile-header .it-card-profile-image .it-card-profile-image-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--bsi-color-background-muted);
}
.it-card.it-card-profile .it-card-body {
  margin-top: var(--bsi-card-spacing-y);
}
.it-card > .list-group,
.it-card .it-card-body > .list-group {
  margin-top: var(--bsi-spacing-s);
  margin-bottom: var(--bsi-spacing-s);
  border-top: var(--bsi-card-border-separator-width) solid var(--bsi-card-border-color);
  border-bottom: var(--bsi-card-border-separator-width) solid var(--bsi-card-border-color);
}
.it-card > .list-group .list-group-item,
.it-card .it-card-body > .list-group .list-group-item {
  padding: var(--bsi-spacing-xs) 0;
}
.it-card > .list-group:first-child,
.it-card .it-card-body > .list-group:first-child {
  border-top-width: 0;
  border-top-left-radius: var(--bsi-card-border-radius-inner);
  border-top-right-radius: var(--bsi-card-border-radius-inner);
}
.it-card > .list-group:last-child,
.it-card .it-card-body > .list-group:last-child {
  border-bottom-width: 0;
  border-bottom-right-radius: var(--bsi-card-border-radius-inner);
  border-bottom-left-radius: var(--bsi-card-border-radius-inner);
}
.it-card > .it-card-header + .list-group,
.it-card > .list-group + .it-card-footer {
  border-top: 0;
}
.it-card {
  /* stylelint-disable-next-line no-duplicate-selectors */
}
.it-card.it-card-image {
  border-top-left-radius: var(--bsi-card-border-radius-inner);
  border-top-right-radius: var(--bsi-card-border-radius-inner);
  border-bottom-right-radius: var(--bsi-card-border-radius-inner);
  border-bottom-left-radius: var(--bsi-card-border-radius-inner);
}
.it-card.it-card-image figure {
  margin-bottom: 0;
}
.it-card.it-card-image .it-card-image-wrapper {
  order: -1;
}
.it-card.it-card-image.rounded .it-card-image-wrapper img {
  border-top-left-radius: var(--bsi-card-border-radius-inner);
  border-top-right-radius: var(--bsi-card-border-radius-inner);
}
.it-card.it-card-image.rounded.it-card-border-top .it-card-image-wrapper:first-child, .it-card.it-card-image.rounded.it-card-border-top .it-card-image-wrapper:nth-child(2) {
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}
.it-card.it-card-image:not(.it-card-inline) .it-card-image-wrapper {
  display: block;
  width: calc(100% + 2 * var(--bsi-card-spacing-x));
  margin-right: calc(-1 * var(--bsi-card-spacing-x));
  margin-bottom: calc(0.5 * var(--bsi-card-spacing-y));
  margin-left: calc(-1 * var(--bsi-card-spacing-x));
  overflow: hidden;
}
.it-card.it-card-border-top::after {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  content: "";
  display: block;
  height: var(--bsi-card-border-top-width);
  background: var(--bsi-card-border-top-color);
}
.it-card.it-card-border-top.rounded::after {
  border-radius: 3px 3px 0 0;
}
.it-card.it-card-border-top.it-card-border-top-primary::after {
  --bsi-card-border-top-color: var(--bsi-color-border-primary);
}
.it-card.it-card-border-top.it-card-border-top-secondary::after {
  --bsi-card-border-top-color: var(--bsi-color-border-secondary);
}
.it-card.it-card-border-top.it-card-border-top-success::after {
  --bsi-card-border-top-color: var(--bsi-color-border-success);
}
.it-card.it-card-border-top.it-card-border-top-warning::after {
  --bsi-card-border-top-color: var(--bsi-color-border-warning);
}
.it-card.it-card-border-top.it-card-border-top-danger::after {
  --bsi-card-border-top-color: var(--bsi-color-border-danger);
}
.it-card {
  /* stylelint-disable-next-line no-duplicate-selectors */
}
.it-card.it-card-profile {
  /* stylelint-disable-next-line no-duplicate-selectors */
}
.it-card.it-card-profile .it-card-profile-header {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap-reverse;
  justify-content: space-between;
  align-items: center;
  margin-top: calc(2 * var(--bsi-card-title-spacer-y));
  margin-bottom: var(--bsi-card-title-spacer-y);
  padding: 0 var(--bsi-card-spacer-x);
}
.it-card.it-card-profile .it-card-profile-header > * {
  margin: 0.5rem;
}
@supports (gap: 1rem) {
  .it-card.it-card-profile .it-card-profile-header {
    gap: 1rem;
  }
  .it-card.it-card-profile .it-card-profile-header > * {
    margin: 0;
  }
}
.it-card.it-card-profile .it-card-profile-header .it-card-profile {
  flex: 1;
  max-width: 100%;
}
.it-card.it-card-profile .it-card-profile-header .it-card-profile .it-card-profile-name {
  margin-bottom: 0.25rem;
}
.it-card.it-card-profile .it-card-profile-header .it-card-profile .it-card-profile-role,
.it-card.it-card-profile .it-card-profile-header .it-card-profile .it-card-place-type {
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.5rem;
  margin-bottom: 0;
}
.it-card.it-card-profile .it-card-profile-header {
  /* stylelint-disable-next-line no-duplicate-selectors */
}
.it-card.it-card-profile .it-card-profile-header .it-card-profile-image {
  max-width: 80px;
  max-height: 80px;
  /* stylelint-disable-next-line no-duplicate-selectors */
}
.it-card.it-card-profile .it-card-profile-header .it-card-profile-image figure {
  margin-bottom: 0;
}
.it-card.it-card-profile .it-card-profile-header .it-card-profile-image {
  /* stylelint-disable-next-line no-duplicate-selectors */
}
.it-card.it-card-profile .it-card-profile-header .it-card-profile-image .it-card-profile-image-icon-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: hsl(210, 62%, 97%);
}
.it-card.it-card-banner {
  padding: var(--bsi-spacing-m);
  align-items: center;
  text-align: center;
  gap: var(--bsi-spacing-s);
}
@media (min-width: 992px) {
  .it-card.it-card-banner {
    padding: var(--bsi-spacing-xl);
  }
}
.it-card.it-card-banner .it-card-title {
  margin-bottom: 0;
}
.it-card.it-card-banner .it-card-body .it-card-subtitle {
  font-weight: var(--bsi-font-weight-weak);
}
.it-card.it-card-banner .it-card-banner-icon-wrapper {
  order: -1;
}
.it-card.it-card-banner .it-card-footer {
  padding-top: var(--bsi-card-spacing-y);
  border-top: 0;
}
.it-card.it-card-inline .it-card-inline-content, .it-card.it-card-inline-mini .it-card-inline-content {
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: var(--bsi-card-spacing-y) var(--bsi-card-spacing-x);
}
@media (min-width: 768px) {
  .it-card.it-card-inline {
    flex-direction: row;
    flex: 1;
  }
  .it-card.it-card-inline.it-card-inline-reverse .it-card-image-wrapper {
    order: 1;
  }
  .it-card.it-card-inline:not(.it-card-inline-mini) {
    min-height: var(--bsi-card-inline-min-height);
  }
  .it-card.it-card-inline:not(.it-card-inline-mini).it-card-image .it-card-image-wrapper, .it-card.it-card-inline:not(.it-card-inline-mini).it-card-image .it-card-inline-content {
    width: 50%;
  }
}
.it-card.it-card-inline:not(.it-card-banner) {
  padding-bottom: 0 !important;
}
.it-card.it-card-inline.it-card-image .it-card-image-wrapper {
  position: relative;
  width: 100%;
  overflow: hidden;
}
.it-card.it-card-inline.it-card-image .ratio {
  height: 100%;
}
.it-card.it-card-inline.it-card-image.rounded .it-card-image-wrapper img {
  border-top-right-radius: 0;
  border-bottom-left-radius: var(--bsi-card-border-radius-inner);
}
.it-card.it-card-inline.it-card-image.rounded.it-card-inline-reverse .it-card-image-wrapper img {
  border-top-left-radius: 0;
  border-top-right-radius: var(--bsi-card-border-radius-inner);
  border-bottom-left-radius: 0;
  border-bottom-right-radius: var(--bsi-card-border-radius-inner);
}
.it-card.it-card-inline.it-card-inline-mini {
  flex-direction: row;
  min-height: var(--bsi-card-inline-mini-min-height);
}
.it-card.it-card-inline.it-card-inline-mini.it-card-inline-reverse .it-card-image-wrapper {
  order: 1;
}
.it-card.it-card-inline.it-card-inline-mini .it-card-inline-content {
  display: flex;
  justify-content: space-between;
  width: 75%;
}
.it-card.it-card-inline.it-card-inline-mini .it-card-image-wrapper {
  width: 25%;
}
@media (min-width: 768px) {
  .it-card.it-card-inline.it-card-banner {
    flex-direction: row;
    align-items: flex-start;
    text-align: left;
  }
}
.it-card.it-card-inline.it-card-banner .it-card-inline-content {
  padding: 0;
  gap: var(--bsi-spacing-xs);
}
.it-card.it-card-inline.it-card-banner.it-card-inline-reverse {
  order: inherit;
}
.it-card.it-card-inline.it-card-banner.it-card-inline-reverse .it-card-banner-icon-wrapper {
  order: 1;
}
.it-card.it-card-inline.it-card-banner.it-card-inline-mini {
  flex-direction: row;
  text-align: left;
  align-items: flex-start;
}

ul.it-card-list {
  margin: 0;
  padding: 0;
  list-style-type: none;
}
ul.it-card-list > li.col,
ul.it-card-list > li[class*=col-] {
  display: block;
  margin-top: 0;
  border: none;
  background: transparent;
  transition: none;
}

.it-card-group {
  --bsi-card-group-gap: 24px;
  display: flex;
  flex-wrap: wrap;
  gap: var(--bsi-card-group-gap);
}
.it-card-group > .it-card {
  margin: 0;
}
@supports (container-type: inline-size) {
  .it-card-group.it-card-group-container-aware {
    container-type: inline-size;
  }
  .it-card-group.it-card-group-container-aware > .it-card {
    flex: 1 0 100%;
  }
  @media (min-width: 576px) {
    .it-card-group.it-card-group-container-aware > .it-card {
      flex: auto;
    }
  }
  @container (min-width: 500px) {
    .it-card-group.it-card-group-container-aware > .it-card {
      flex: 0 0 calc(50% - 0.5 * var(--bsi-card-group-gap));
    }
  }
  @container (min-width: 800px) {
    .it-card-group.it-card-group-container-aware > .it-card {
      flex: 0 0 calc(33.333% - 0.667 * var(--bsi-card-group-gap));
    }
  }
  @container (min-width: 1100px) {
    .it-card-group.it-card-group-container-aware > .it-card {
      flex: 0 0 calc(25% - 0.75 * var(--bsi-card-group-gap));
    }
  }
  @container (min-width: 500px) {
    .it-card-group.it-card-group-container-aware.it-card-group-2-cols > .it-card {
      flex: 0 0 calc(50% - 0.5 * var(--bsi-card-group-gap));
    }
  }
  @container (min-width: 800px) {
    .it-card-group.it-card-group-container-aware.it-card-group-3-cols > .it-card {
      flex: 0 0 calc(33.333% - 0.667 * var(--bsi-card-group-gap));
    }
  }
}
.it-card-group {
  /* stylelint-disable-next-line no-duplicate-selectors */
}
.it-card-group > .it-card {
  flex: 0 0 calc(100% - var(--bsi-card-group-gap));
}
@media (min-width: 576px) {
  .it-card-group > .it-card {
    flex: 0 0 calc(50% - 0.5 * var(--bsi-card-group-gap));
  }
}
@media (min-width: 768px) {
  .it-card-group > .it-card {
    flex: 0 0 calc(33.333% - 0.667 * var(--bsi-card-group-gap));
  }
}
@media (min-width: 992px) {
  .it-card-group > .it-card {
    flex: 0 0 calc(25% - 0.75 * var(--bsi-card-group-gap));
  }
}
@media (min-width: 576px) {
  .it-card-group.it-card-group-2-cols > .it-card, .it-card-group.it-card-group-3-cols > .it-card, .it-card-group.it-card-group-4-cols > .it-card {
    flex: 0 0 calc(50% - 0.5 * var(--bsi-card-group-gap));
  }
}
@media (min-width: 768px) {
  .it-card-group.it-card-group-3-cols > .it-card {
    flex: 0 0 calc(33.333% - 0.667 * var(--bsi-card-group-gap));
  }
}
@media (min-width: 768px) {
  .it-card-group.it-card-group-4-cols > .it-card {
    flex: 0 0 calc(33.333% - 0.667 * var(--bsi-card-group-gap));
  }
}
@media (min-width: 992px) {
  .it-card-group.it-card-group-4-cols > .it-card {
    flex: 0 0 calc(25% - 0.75 * var(--bsi-card-group-gap));
  }
}
.it-card-group.it-card-group-equal-height > .it-card {
  height: 100%;
}
.it-card-group.it-card-group-center {
  justify-content: center;
}
.it-card-group.it-card-group-end {
  justify-content: flex-end;
}

*,
*::before,
*::after {
  box-sizing: border-box;
}

@media (prefers-reduced-motion: no-preference) {
  :root {
    scroll-behavior: smooth;
  }
}

body {
  margin: 0;
  -webkit-text-size-adjust: 100%;
  -webkit-tap-highlight-color: hsla(0, 0%, 0%, 0);
}

hr {
  margin: 1rem 0;
  color: inherit;
  border: 0;
  border-top: 1px solid;
  opacity: 0.25;
}

p {
  margin-top: 0;
  margin-bottom: var(--bsi-paragraph-spacing);
}

abbr[title] {
  text-decoration: underline dotted;
  cursor: help;
  text-decoration-skip-ink: none;
}

address {
  margin-bottom: var(--bsi-spacing-s);
  font-style: normal;
  line-height: inherit;
}

ol,
ul {
  padding-left: var(--bsi-spacing-l);
}

ol,
ul,
dl {
  margin-top: 0;
  margin-bottom: var(--bsi-spacing-s);
}

ol ol,
ul ul,
ol ul,
ul ol {
  margin-bottom: 0;
}

dt {
  font-weight: var(--bsi-font-weight-strong);
}

dd {
  margin-bottom: var(--bsi-spacing-xxs);
  margin-left: 0;
}

blockquote {
  margin: 0 0 var(--bsi-spacing-s);
}

sub,
sup {
  position: relative;
  font-size: var(--bsi-font-size-1);
  line-height: 0;
  vertical-align: baseline;
}

sub {
  bottom: -0.25em;
}

sup {
  top: -0.5em;
}

a {
  color: var(--bsi-color-link);
  text-decoration: underline;
  text-decoration-skip-ink: auto;
  text-underline-offset: 2px;
}
a:hover {
  color: var(--bsi-color-link-hover);
  cursor: pointer;
}

pre,
code,
kbd,
samp {
  font-size: 1em;
}

pre {
  display: block;
  margin-top: 0;
  margin-bottom: var(--bsi-paragraph-spacing);
  overflow: auto;
}
pre code {
  word-break: normal;
}

a > code {
  color: inherit;
}

figure {
  margin: 0 0 1rem;
}

img,
svg {
  vertical-align: middle;
}

table {
  caption-side: bottom;
  border-collapse: collapse;
}

caption {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  color: hsl(210, 17%, 44%);
  text-align: left;
}

th {
  text-align: inherit;
  text-align: -webkit-match-parent;
}

thead,
tbody,
tfoot,
tr,
td,
th {
  border-color: inherit;
  border-style: solid;
  border-width: 0;
}

label {
  display: inline-block;
}

button {
  border-radius: 0;
}

button:focus:not(:focus-visible) {
  outline: 0;
}

input,
button,
select,
optgroup,
textarea {
  margin: 0;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
}

button,
select {
  text-transform: none;
}

[role=button] {
  cursor: pointer;
}

select {
  word-wrap: normal;
}
select:disabled {
  opacity: 1;
}

[list]:not([type=date]):not([type=datetime-local]):not([type=month]):not([type=week]):not([type=time])::-webkit-calendar-picker-indicator {
  display: none !important;
}

button,
[type=button],
[type=reset],
[type=submit] {
  -webkit-appearance: button;
}
button:not(:disabled),
[type=button]:not(:disabled),
[type=reset]:not(:disabled),
[type=submit]:not(:disabled) {
  cursor: pointer;
}

::-moz-focus-inner {
  padding: 0;
  border-style: none;
}

textarea {
  resize: vertical;
}

fieldset {
  min-width: 0;
  padding: 0;
  margin: 0;
  border: 0;
}

::-webkit-datetime-edit-fields-wrapper,
::-webkit-datetime-edit-text,
::-webkit-datetime-edit-minute,
::-webkit-datetime-edit-hour-field,
::-webkit-datetime-edit-day-field,
::-webkit-datetime-edit-month-field,
::-webkit-datetime-edit-year-field {
  padding: 0;
}

::-webkit-inner-spin-button {
  height: auto;
}

[type=search] {
  outline-offset: -2px;
  -webkit-appearance: textfield;
}

/* rtl:raw:
[type="tel"],
[type="url"],
[type="email"],
[type="number"] {
  direction: ltr;
}
*/
::-webkit-search-decoration {
  -webkit-appearance: none;
}

::-webkit-color-swatch-wrapper {
  padding: 0;
}

::file-selector-button {
  font: inherit;
  -webkit-appearance: button;
}

output {
  display: inline-block;
}

iframe {
  border: 0;
}

summary {
  display: list-item;
  cursor: pointer;
}

progress {
  vertical-align: baseline;
}

[hidden] {
  display: none !important;
}

body {
  font-family: var(--bsi-font-sans);
}

p,
ul,
ol,
dl {
  font-size: var(--bsi-body-font-size);
  line-height: var(--bsi-body-leading);
}

caption,
figcaption {
  font-size: var(--bsi-caption-font-size);
  line-height: var(--bsi-caption-leading);
}

b,
strong {
  font-weight: var(--bsi-font-weight-strong);
}

mark,
code {
  padding: 0 var(--bsi-spacing-3xs);
}

mark {
  background-color: var(--bsi-highlight-background);
}

pre,
code,
kbd,
samp {
  border-radius: var(--bsi-radius-smooth);
  font-family: var(--bsi-font-mono);
}

ins,
del {
  position: relative;
  display: flex;
  align-items: center;
  padding: var(--bsi-spacing-3xs) var(--bsi-spacing-m);
  font: var(--bsi-body-font-size);
  text-decoration: none;
}

ins {
  background-color: var(--bsi-ins-background);
  text-decoration: none;
}

del {
  background-color: var(--bsi-del-background);
}

del::before,
ins::before {
  position: absolute;
  left: 0.5rem;
}

del::before {
  content: "−";
}

ins::before {
  content: "+";
}

kbd {
  margin: 0 2px;
  padding: 2px var(--bsi-spacing-3xs);
  border: var(--bsi-border-base) solid var(--bsi-color-border-subtle);
  background-color: var(--bsi-color-background-secondary-lighter);
  box-shadow: var(--bsi-elevation-low), 0 2px 0 0 hsla(255, 0%, 100%, 0.7) inset;
  color: var(--bsi-color-text-secondary);
  font-size: var(--bsi-font-size-1);
  font-weight: var(--bsi-font-weight-solid);
  white-space: nowrap;
}
kbd kbd {
  padding: 0;
  font-size: var(--bsi-code-font-size);
}

small,
.small {
  font-size: var(--bsi-caption-font-size);
}

.x-small {
  font-size: var(--bsi-font-size-1);
}

h1,
.h1 {
  font-size: var(--bsi-heading-1-font-size);
}

h2,
.h2 {
  font-size: var(--bsi-heading-2-font-size);
}

h3,
.h3 {
  font-size: var(--bsi-heading-3-font-size);
}

h4,
.h4 {
  font-size: var(--bsi-heading-4-font-size);
}

h5,
.h5 {
  font-size: var(--bsi-heading-5-font-size);
}

h6,
.h6 {
  font-size: var(--bsi-heading-6-font-size);
}

h1,
.h1,
h2,
.h2,
h3,
.h3,
h4,
.h4,
h5,
.h5,
h6,
.h6 {
  margin-top: 0;
  margin-bottom: var(--bsi-heading-spacing);
  line-height: var(--bsi-heading-leading);
}
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + h1, p + h1,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + .h1,
p + .h1,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + h2,
p + h2,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + .h2,
p + .h2,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + h3,
p + h3,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + .h3,
p + .h3,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + h4,
p + h4,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + .h4,
p + .h4,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + h5,
p + h5,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + .h5,
p + .h5,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + h6,
p + h6,
:is(h1, .h1, h2, .h2, h3, .h3, h4, .h4, h5, .h5, h6, .h6) + .h6,
p + .h6 {
  padding-top: var(--bsi-spacing-s);
}

h1,
h2,
h3,
.h1,
.h2,
.h3 {
  font-weight: var(--bsi-heading-font-weight);
}

h4,
h5,
h6,
.h4,
.h5,
.h6 {
  font-weight: var(--bsi-heading-font-weight-weak);
}

h1,
.h1,
.display-1 {
  letter-spacing: -1px;
}

.font-serif {
  font-family: var(--bsi-font-serif) !important;
}

.font-sans-serif {
  font-family: var(--bsi-font-sans) !important;
}

.font-monospace {
  font-family: var(--bsi-font-mono) !important;
}

.display-1 {
  font-size: var(--bsi-display-font-size);
  font-weight: var(--bsi-heading-font-weight);
  line-height: var(--bsi-heading-leading);
}

.lead {
  font-size: var(--bsi-lead-font-size);
  font-weight: var(--bsi-lead-font-weight);
  line-height: var(--bsi-lead-leading);
}

blockquote,
.blockquote {
  margin: var(--bsi-spacing-m) 0;
  margin-left: var(--bsi-spacin-inline-xs);
  padding: var(--bsi-spacing-s);
  border-left: var(--bsi-border-thick) solid var(--bsi-border-color-secondary);
  font-size: var(--bsi-body-font-size);
  line-height: var(--bsi-body-leading);
}
blockquote > :last-child,
.blockquote > :last-child {
  margin-bottom: 0;
}
blockquote.text-end,
.blockquote.text-end {
  margin-right: var(--bsi-spacin-inline-xs);
  border-right: var(--bsi-border-thick) solid var(--bsi-border-color-secondary);
}
blockquote.text-center, blockquote.text-end, blockquote.blockquote-simple,
.blockquote.text-center,
.blockquote.text-end,
.blockquote.blockquote-simple {
  margin-left: 0;
  border-right: none;
  border-left: none;
}
blockquote.text-center, blockquote.blockquote-simple,
.blockquote.text-center,
.blockquote.blockquote-simple {
  padding: 0;
}
blockquote.blockquote-simple,
.blockquote.blockquote-simple {
  font-style: italic;
}
blockquote.blockquote-card,
.blockquote.blockquote-card {
  margin-left: 0;
  padding: var(--bsi-spacing-m);
  background-color: var(--bsi-color-background-inverse);
  box-shadow: var(--bsi-elevation-low);
}
blockquote.blockquote-card .blockquote-footer,
.blockquote.blockquote-card .blockquote-footer {
  font-size: inherit;
}
blockquote.blockquote-card .blockquote-footer:before,
.blockquote.blockquote-card .blockquote-footer:before {
  content: none;
}
blockquote.blockquote-card.dark,
.blockquote.blockquote-card.dark {
  border-left: none;
  background-color: var(--bsi-color-background-primary);
  color: var(--bsi-color-text-inverse);
}
blockquote.blockquote-card.dark .blockquote-footer,
.blockquote.blockquote-card.dark .blockquote-footer {
  color: var(--bsi-color-text-inverse);
}

.blockquote-footer {
  margin-top: 0;
  margin-bottom: var(--bsi-spacing-s);
  color: var(--bsi-color-text-secondary);
  font-size: var(--bsi-label-font-size);
}
.blockquote-footer::before {
  content: "— ";
}
.bg-dark .blockquote-footer {
  color: var(--bsi-color-text-muted);
}

.initialism {
  font-size: var(--bsi-label-font-size-s);
  text-transform: uppercase;
}

.rounded {
  border-radius: var(--bsi-radius-smooth) !important;
}

.d-none {
  display: none !important;
}

.shadow-sm {
  box-shadow: var(--bsi-elevation-low) !important;
}

.shadow-md {
  box-shadow: var(--bsi-elevation-medium) !important;
}

.shadow-lg {
  box-shadow: var(--bsi-elevation-high) !important;
}

.shadow-none {
  box-shadow: none !important;
}

.border {
  border: var(--bsi-border-base) var(--bsi-border-style) var(--bsi-border-color) !important;
}

.border-0 {
  border: 0 !important;
}

.border-top {
  border-top: var(--bsi-border-base) var(--bsi-border-style) var(--bsi-border-color) !important;
}

.border-top-0 {
  border-top: 0 !important;
}

.p-0 {
  padding: 0 !important;
}

.p-1 {
  padding: 0.25rem !important;
}

.p-2 {
  padding: 0.5rem !important;
}

.p-3 {
  padding: 1rem !important;
}

.p-4 {
  padding: 1.5rem !important;
}

.p-5 {
  padding: 3rem !important;
}

.px-0 {
  padding-right: 0 !important;
  padding-left: 0 !important;
}

.px-1 {
  padding-right: 0.25rem !important;
  padding-left: 0.25rem !important;
}

.px-2 {
  padding-right: 0.5rem !important;
  padding-left: 0.5rem !important;
}

.px-3 {
  padding-right: 1rem !important;
  padding-left: 1rem !important;
}

.px-4 {
  padding-right: 1.5rem !important;
  padding-left: 1.5rem !important;
}

.px-5 {
  padding-right: 3rem !important;
  padding-left: 3rem !important;
}

.py-0 {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.py-1 {
  padding-top: 0.25rem !important;
  padding-bottom: 0.25rem !important;
}

.py-2 {
  padding-top: 0.5rem !important;
  padding-bottom: 0.5rem !important;
}

.py-3 {
  padding-top: 1rem !important;
  padding-bottom: 1rem !important;
}

.py-4 {
  padding-top: 1.5rem !important;
  padding-bottom: 1.5rem !important;
}

.py-5 {
  padding-top: 3rem !important;
  padding-bottom: 3rem !important;
}

.pt-0 {
  padding-top: 0 !important;
}

.pt-1 {
  padding-top: 0.25rem !important;
}

.pt-2 {
  padding-top: 0.5rem !important;
}

.pt-3 {
  padding-top: 1rem !important;
}

.pt-4 {
  padding-top: 1.5rem !important;
}

.pt-5 {
  padding-top: 3rem !important;
}

@media (min-width: 576px) {
  .d-sm-none {
    display: none !important;
  }
  .p-sm-0 {
    padding: 0 !important;
  }
  .p-sm-1 {
    padding: 0.25rem !important;
  }
  .p-sm-2 {
    padding: 0.5rem !important;
  }
  .p-sm-3 {
    padding: 1rem !important;
  }
  .p-sm-4 {
    padding: 1.5rem !important;
  }
  .p-sm-5 {
    padding: 3rem !important;
  }
  .px-sm-0 {
    padding-right: 0 !important;
    padding-left: 0 !important;
  }
  .px-sm-1 {
    padding-right: 0.25rem !important;
    padding-left: 0.25rem !important;
  }
  .px-sm-2 {
    padding-right: 0.5rem !important;
    padding-left: 0.5rem !important;
  }
  .px-sm-3 {
    padding-right: 1rem !important;
    padding-left: 1rem !important;
  }
  .px-sm-4 {
    padding-right: 1.5rem !important;
    padding-left: 1.5rem !important;
  }
  .px-sm-5 {
    padding-right: 3rem !important;
    padding-left: 3rem !important;
  }
  .py-sm-0 {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }
  .py-sm-1 {
    padding-top: 0.25rem !important;
    padding-bottom: 0.25rem !important;
  }
  .py-sm-2 {
    padding-top: 0.5rem !important;
    padding-bottom: 0.5rem !important;
  }
  .py-sm-3 {
    padding-top: 1rem !important;
    padding-bottom: 1rem !important;
  }
  .py-sm-4 {
    padding-top: 1.5rem !important;
    padding-bottom: 1.5rem !important;
  }
  .py-sm-5 {
    padding-top: 3rem !important;
    padding-bottom: 3rem !important;
  }
  .pt-sm-0 {
    padding-top: 0 !important;
  }
  .pt-sm-1 {
    padding-top: 0.25rem !important;
  }
  .pt-sm-2 {
    padding-top: 0.5rem !important;
  }
  .pt-sm-3 {
    padding-top: 1rem !important;
  }
  .pt-sm-4 {
    padding-top: 1.5rem !important;
  }
  .pt-sm-5 {
    padding-top: 3rem !important;
  }
}
@media (min-width: 768px) {
  .d-md-none {
    display: none !important;
  }
  .p-md-0 {
    padding: 0 !important;
  }
  .p-md-1 {
    padding: 0.25rem !important;
  }
  .p-md-2 {
    padding: 0.5rem !important;
  }
  .p-md-3 {
    padding: 1rem !important;
  }
  .p-md-4 {
    padding: 1.5rem !important;
  }
  .p-md-5 {
    padding: 3rem !important;
  }
  .px-md-0 {
    padding-right: 0 !important;
    padding-left: 0 !important;
  }
  .px-md-1 {
    padding-right: 0.25rem !important;
    padding-left: 0.25rem !important;
  }
  .px-md-2 {
    padding-right: 0.5rem !important;
    padding-left: 0.5rem !important;
  }
  .px-md-3 {
    padding-right: 1rem !important;
    padding-left: 1rem !important;
  }
  .px-md-4 {
    padding-right: 1.5rem !important;
    padding-left: 1.5rem !important;
  }
  .px-md-5 {
    padding-right: 3rem !important;
    padding-left: 3rem !important;
  }
  .py-md-0 {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }
  .py-md-1 {
    padding-top: 0.25rem !important;
    padding-bottom: 0.25rem !important;
  }
  .py-md-2 {
    padding-top: 0.5rem !important;
    padding-bottom: 0.5rem !important;
  }
  .py-md-3 {
    padding-top: 1rem !important;
    padding-bottom: 1rem !important;
  }
  .py-md-4 {
    padding-top: 1.5rem !important;
    padding-bottom: 1.5rem !important;
  }
  .py-md-5 {
    padding-top: 3rem !important;
    padding-bottom: 3rem !important;
  }
  .pt-md-0 {
    padding-top: 0 !important;
  }
  .pt-md-1 {
    padding-top: 0.25rem !important;
  }
  .pt-md-2 {
    padding-top: 0.5rem !important;
  }
  .pt-md-3 {
    padding-top: 1rem !important;
  }
  .pt-md-4 {
    padding-top: 1.5rem !important;
  }
  .pt-md-5 {
    padding-top: 3rem !important;
  }
}
@media (min-width: 992px) {
  .d-lg-none {
    display: none !important;
  }
  .p-lg-0 {
    padding: 0 !important;
  }
  .p-lg-1 {
    padding: 0.25rem !important;
  }
  .p-lg-2 {
    padding: 0.5rem !important;
  }
  .p-lg-3 {
    padding: 1rem !important;
  }
  .p-lg-4 {
    padding: 1.5rem !important;
  }
  .p-lg-5 {
    padding: 3rem !important;
  }
  .px-lg-0 {
    padding-right: 0 !important;
    padding-left: 0 !important;
  }
  .px-lg-1 {
    padding-right: 0.25rem !important;
    padding-left: 0.25rem !important;
  }
  .px-lg-2 {
    padding-right: 0.5rem !important;
    padding-left: 0.5rem !important;
  }
  .px-lg-3 {
    padding-right: 1rem !important;
    padding-left: 1rem !important;
  }
  .px-lg-4 {
    padding-right: 1.5rem !important;
    padding-left: 1.5rem !important;
  }
  .px-lg-5 {
    padding-right: 3rem !important;
    padding-left: 3rem !important;
  }
  .py-lg-0 {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }
  .py-lg-1 {
    padding-top: 0.25rem !important;
    padding-bottom: 0.25rem !important;
  }
  .py-lg-2 {
    padding-top: 0.5rem !important;
    padding-bottom: 0.5rem !important;
  }
  .py-lg-3 {
    padding-top: 1rem !important;
    padding-bottom: 1rem !important;
  }
  .py-lg-4 {
    padding-top: 1.5rem !important;
    padding-bottom: 1.5rem !important;
  }
  .py-lg-5 {
    padding-top: 3rem !important;
    padding-bottom: 3rem !important;
  }
  .pt-lg-0 {
    padding-top: 0 !important;
  }
  .pt-lg-1 {
    padding-top: 0.25rem !important;
  }
  .pt-lg-2 {
    padding-top: 0.5rem !important;
  }
  .pt-lg-3 {
    padding-top: 1rem !important;
  }
  .pt-lg-4 {
    padding-top: 1.5rem !important;
  }
  .pt-lg-5 {
    padding-top: 3rem !important;
  }
}
@media (min-width: 1200px) {
  .d-xl-none {
    display: none !important;
  }
  .p-xl-0 {
    padding: 0 !important;
  }
  .p-xl-1 {
    padding: 0.25rem !important;
  }
  .p-xl-2 {
    padding: 0.5rem !important;
  }
  .p-xl-3 {
    padding: 1rem !important;
  }
  .p-xl-4 {
    padding: 1.5rem !important;
  }
  .p-xl-5 {
    padding: 3rem !important;
  }
  .px-xl-0 {
    padding-right: 0 !important;
    padding-left: 0 !important;
  }
  .px-xl-1 {
    padding-right: 0.25rem !important;
    padding-left: 0.25rem !important;
  }
  .px-xl-2 {
    padding-right: 0.5rem !important;
    padding-left: 0.5rem !important;
  }
  .px-xl-3 {
    padding-right: 1rem !important;
    padding-left: 1rem !important;
  }
  .px-xl-4 {
    padding-right: 1.5rem !important;
    padding-left: 1.5rem !important;
  }
  .px-xl-5 {
    padding-right: 3rem !important;
    padding-left: 3rem !important;
  }
  .py-xl-0 {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }
  .py-xl-1 {
    padding-top: 0.25rem !important;
    padding-bottom: 0.25rem !important;
  }
  .py-xl-2 {
    padding-top: 0.5rem !important;
    padding-bottom: 0.5rem !important;
  }
  .py-xl-3 {
    padding-top: 1rem !important;
    padding-bottom: 1rem !important;
  }
  .py-xl-4 {
    padding-top: 1.5rem !important;
    padding-bottom: 1.5rem !important;
  }
  .py-xl-5 {
    padding-top: 3rem !important;
    padding-bottom: 3rem !important;
  }
  .pt-xl-0 {
    padding-top: 0 !important;
  }
  .pt-xl-1 {
    padding-top: 0.25rem !important;
  }
  .pt-xl-2 {
    padding-top: 0.5rem !important;
  }
  .pt-xl-3 {
    padding-top: 1rem !important;
  }
  .pt-xl-4 {
    padding-top: 1.5rem !important;
  }
  .pt-xl-5 {
    padding-top: 3rem !important;
  }
}
@media (min-width: 1400px) {
  .d-xxl-none {
    display: none !important;
  }
  .p-xxl-0 {
    padding: 0 !important;
  }
  .p-xxl-1 {
    padding: 0.25rem !important;
  }
  .p-xxl-2 {
    padding: 0.5rem !important;
  }
  .p-xxl-3 {
    padding: 1rem !important;
  }
  .p-xxl-4 {
    padding: 1.5rem !important;
  }
  .p-xxl-5 {
    padding: 3rem !important;
  }
  .px-xxl-0 {
    padding-right: 0 !important;
    padding-left: 0 !important;
  }
  .px-xxl-1 {
    padding-right: 0.25rem !important;
    padding-left: 0.25rem !important;
  }
  .px-xxl-2 {
    padding-right: 0.5rem !important;
    padding-left: 0.5rem !important;
  }
  .px-xxl-3 {
    padding-right: 1rem !important;
    padding-left: 1rem !important;
  }
  .px-xxl-4 {
    padding-right: 1.5rem !important;
    padding-left: 1.5rem !important;
  }
  .px-xxl-5 {
    padding-right: 3rem !important;
    padding-left: 3rem !important;
  }
  .py-xxl-0 {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }
  .py-xxl-1 {
    padding-top: 0.25rem !important;
    padding-bottom: 0.25rem !important;
  }
  .py-xxl-2 {
    padding-top: 0.5rem !important;
    padding-bottom: 0.5rem !important;
  }
  .py-xxl-3 {
    padding-top: 1rem !important;
    padding-bottom: 1rem !important;
  }
  .py-xxl-4 {
    padding-top: 1.5rem !important;
    padding-bottom: 1.5rem !important;
  }
  .py-xxl-5 {
    padding-top: 3rem !important;
    padding-bottom: 3rem !important;
  }
  .pt-xxl-0 {
    padding-top: 0 !important;
  }
  .pt-xxl-1 {
    padding-top: 0.25rem !important;
  }
  .pt-xxl-2 {
    padding-top: 0.5rem !important;
  }
  .pt-xxl-3 {
    padding-top: 1rem !important;
  }
  .pt-xxl-4 {
    padding-top: 1.5rem !important;
  }
  .pt-xxl-5 {
    padding-top: 3rem !important;
  }
}
@media print {
  .d-print-none {
    display: none !important;
  }
}
/**
 * Copied from bsi reboot
 */
*,
*::before,
*::after {
  box-sizing: border-box;
}

p {
  margin-top: 0;
  margin-bottom: var(--bsi-paragraph-spacing);
}
p:last-of-type {
  margin-bottom: 0;
}

address {
  margin-bottom: var(--bsi-spacing-s);
  font-style: normal;
  line-height: inherit;
}

ol,
ul {
  padding-left: var(--bsi-spacing-l);
}

ol,
ul,
dl {
  margin-top: 0;
  margin-bottom: var(--bsi-spacing-s);
}

/* END copied from bsi reboot */
::slotted(figure),
::slotted(.it-card-profile-image-icon-wrapper) {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.it-card .it-card-title.it-card-title-icon {
  display: block;
}
.it-card .it-card-title.it-card-title-icon slot[name=title] {
  display: flex;
  justify-content: space-between;
}
.it-card .it-card-title.it-card-title-icon ::slotted(a) {
  display: flex;
  width: 100%;
  justify-content: space-between;
}
.it-card .it-card-title.it-card-title-icon ::slotted(a):focus-visible {
  display: inline-block;
}
.it-card .it-card-footer {
  display: block;
}

.it-card-footer slot[name=footer] {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: flex-end;
  color: var(--bsi-card-footer-text-color);
  gap: var(--bsi-card-spacing-y);
}

::slotted(.it-card-taxonomy) {
  flex-grow: 1;
}

.it-card-place-type {
  margin-top: 0.25rem;
}

.it-card-place-address {
  margin-top: 0.25rem;
  margin-bottom: 0;
  font-size: 1rem;
  line-height: 1.5rem;
}`;

let ItCard = class ItCard extends BaseComponent {
    constructor() {
        super(...arguments);
        this.fullHeight = false;
        this.variant = 'default';
        this.shadow = 'sm';
        this.headingLevel = 'h3';
    }
    handleSlotChange() {
        this.requestUpdate();
    }
    updated(_changedProperties) {
        super.updated(_changedProperties);
        if (this._titleElements.length === 0) {
            this.logger.warn('it-card: no title slot content found');
        }
        if (this.variant && !CARD_VARIANTS.includes(this.variant)) {
            this.logger.warn(`Invalid variant value, falling back to default. Expected one of: ${CARD_VARIANTS.join(', ')}`);
        }
        if (this.borderTop && !CARD_BORDER_COLORS.includes(this.borderTop)) {
            this.logger.warn(`Invalid border-top value, falling back to default. Expected one of: ${CARD_BORDER_COLORS.join(', ')}`);
        }
        if (this.ratio && !CARD_IMAGE_RATIOS.includes(this.ratio)) {
            this.logger.warn(`Invalid ratio value, falling back to default. Expected one of: ${CARD_IMAGE_RATIOS.join(', ')}`);
        }
        if (this.shadow && !CARD_SHADOWS.includes(this.shadow)) {
            this.logger.warn(`Invalid shadow value, falling back to default. Expected one of: ${CARD_SHADOWS.join(', ')}`);
        }
        if (this.border && this.border !== '0') {
            this.logger.warn(`Invalid border value, falling back to default. Expected: '0' or undefined`);
        }
        if (this.headingLevel && !CARD_HEADING_LEVELS.includes(this.headingLevel)) {
            this.logger.warn(`Invalid heading-level value, falling back to default. Expected one of: ${CARD_HEADING_LEVELS.join(', ')}`);
        }
        if (this.headingSize && !CARD_HEADING_SIZES.includes(this.headingSize)) {
            this.logger.warn(`Invalid heading-size value, falling back to default. Expected one of: ${CARD_HEADING_SIZES.join(', ')}`);
        }
    }
    getHeadingLevel() {
        if (CARD_HEADING_LEVELS.includes(this.headingLevel)) {
            return this.headingLevel;
        }
        return 'h3';
    }
    getRatio() {
        if (this.ratio && CARD_IMAGE_RATIOS.includes(this.ratio)) {
            return this.ratio;
        }
        const isInline = this.variant.startsWith('inline');
        return isInline || this.variant === 'location' ? '1x1' : '16x9';
    }
    getImageTemplate() {
        const hasImage = this._imageElements.length > 0;
        if (hasImage && this.variant !== 'profile' && this.variant !== 'location' && !this.variant.includes('banner')) {
            return html `
        <div class="it-card-image-wrapper" part="image-wrapper">
          <div class="ratio ratio-${this.getRatio()}">
            <slot name="image" @slotchange=${this.handleSlotChange}></slot>
          </div>
        </div>
      `;
        }
        if (hasImage && this.variant === 'location') {
            return html `
        <div class="it-card-profile-image ratio ratio-${this.getRatio()}" part="image-wrapper">
          <slot name="image" @slotchange=${this.handleSlotChange}></slot>
        </div>
      `;
        }
        if (hasImage && this.variant.includes('banner')) {
            return html `
        <div class="it-card-banner-icon-wrapper" part="image-wrapper">
          <slot name="image" @slotchange=${this.handleSlotChange}></slot>
        </div>
      `;
        }
        return html `<slot name="image" @slotchange=${this.handleSlotChange}></slot>`;
    }
    render() {
        const shadowClass = CARD_SHADOWS.includes(this.shadow) ? `shadow-${this.shadow}` : 'shadow-sm';
        const borderClass = this.border === '0' ? 'border-0' : 'border';
        const hasSubtitle = this._subtitleElements.length > 0;
        const hasAddress = this._addressElements.length > 0;
        const hasSignature = this._signatureElements.length > 0;
        const hasText = this._textElements.length > 0;
        const hasBody = this._bodyElements.length > 0 || hasText || hasSubtitle || hasSignature;
        const hasImage = this._imageElements.length > 0;
        const hasFooter = this._footerElements.length > 0;
        const hasActions = this._actionsElements.length > 0;
        const hasTitleIcon = this._titleElements.some((el) => el.querySelector('.it-card-title-icon-wrapper') !== null);
        const isInline = this.variant.startsWith('inline');
        const classes = this.composeClass('it-card', 'rounded', shadowClass, borderClass, this.itClass, {
            'it-card-inline': isInline,
            'it-card-inline-reverse': this.variant.endsWith('reverse'),
            'it-card-inline-mini': this.variant.includes('mini'),
            'it-card-profile': this.variant === 'profile' || this.variant === 'location',
            'it-card-banner': this.variant.includes('banner'),
            'it-card-image': hasImage && this.variant !== 'profile' && this.variant !== 'location' && !this.variant.includes('banner'),
            'it-card-height-full': this.fullHeight,
            [`it-card-border-top it-card-border-top-${this.borderTop}`]: this.borderTop !== undefined,
        });
        const titleClasses = this.composeClass({
            'it-card-title': this.variant !== 'profile' && this.variant !== 'location',
            'it-card-profile-name': this.variant === 'profile' || this.variant === 'location',
            'it-card-title-icon': hasTitleIcon,
            h4: this.headingSize === 'sm' ||
                (this.headingSize === undefined &&
                    (this.variant === 'inline-mini' ||
                        this.variant === 'inline-mini-reverse' ||
                        this.variant === 'profile' ||
                        this.variant === 'location')),
            h3: this.headingSize === 'md' ||
                (this.headingSize === undefined &&
                    this.headingLevel !== 'h3' &&
                    !(this.variant === 'inline-mini' ||
                        this.variant === 'inline-mini-reverse' ||
                        this.variant === 'profile' ||
                        this.variant === 'location')),
        });
        const headingTag = unsafeStatic(this.getHeadingLevel());
        const cardTitle = html `
      ${html$1 `
        <${headingTag} class="${titleClasses}" part="title">
          <slot name="title" @slotchange=${this.handleSlotChange}></slot>
        </${headingTag}>
      `}
    `;
        const cardImage = this.getImageTemplate();
        const cardBody = hasBody
            ? html `
          <div class="it-card-body" part="body">
            ${hasSubtitle && this.variant !== 'profile' && this.variant !== 'location'
                ? html `
                  <p class="it-card-subtitle" part="subtitle">
                    <slot name="subtitle" @slotchange=${this.handleSlotChange}></slot>
                  </p>
                `
                : html `<slot name="subtitle" @slotchange=${this.handleSlotChange}></slot>`}
            ${hasSignature
                ? html `
                  <address class="it-card-signature" part="signature">
                    <slot name="signature" @slotchange=${this.handleSlotChange}></slot>
                  </address>
                `
                : html `<slot name="signature" @slotchange=${this.handleSlotChange}></slot>`}
            ${hasText
                ? html `
                  <p class="it-card-text" part="text">
                    <slot name="text" @slotchange=${this.handleSlotChange}></slot>
                  </p>
                `
                : html `<slot name="text" @slotchange=${this.handleSlotChange}></slot>`}
            <slot name="body" @slotchange=${this.handleSlotChange}></slot>
          </div>
        `
            : html `
          <slot name="subtitle" @slotchange=${this.handleSlotChange}></slot>
          <slot name="signature" @slotchange=${this.handleSlotChange}></slot>
          <slot name="text" @slotchange=${this.handleSlotChange}></slot>
          <slot name="body" @slotchange=${this.handleSlotChange}></slot>
        `;
        const cardFooter = hasFooter
            ? html `
          <footer class="it-card-footer ${this.variant === 'location' ? 'border-top pt-3' : ''}" part="footer">
            <slot name="footer" @slotchange=${this.handleSlotChange}></slot>
          </footer>
        `
            : html `<slot name="footer" @slotchange=${this.handleSlotChange}></slot>`;
        const cardActions = hasActions
            ? html `
          <div
            class="it-card-actions"
            role="group"
            part="actions"
            aria-label=${this.actionsAriaLabel ? this.actionsAriaLabel : 'Link correlati:'}
          >
            <slot name="actions" @slotchange=${this.handleSlotChange}></slot>
          </div>
        `
            : html `<slot name="actions" @slotchange=${this.handleSlotChange}></slot>`;
        if (this.variant === 'profile') {
            return html `
        <article class="${classes}" part="card">
          <div class="it-card-profile-header">
            <div class="it-card-profile-content">
              ${cardTitle}
              ${hasSubtitle
                ? html `
                    <p class="it-card-profile-role" part="subtitle">
                      <slot name="subtitle" @slotchange=${this.handleSlotChange}></slot>
                    </p>
                  `
                : html `<slot name="subtitle" @slotchange=${this.handleSlotChange}></slot>`}
            </div>
            ${cardImage}
          </div>
          ${cardBody}${cardFooter}${cardActions}
        </article>
      `;
        }
        if (this.variant === 'location') {
            return html `
        <article class="${classes}" part="card">
          <div class="it-card-profile-header">
            <div class="it-card-profile">
              ${cardTitle}
              ${hasSubtitle
                ? html `
                    <p class="it-card-place-type" part="subtitle">
                      <slot name="subtitle" @slotchange=${this.handleSlotChange}></slot>
                    </p>
                  `
                : html `<slot name="subtitle" @slotchange=${this.handleSlotChange}></slot>`}
              ${hasAddress
                ? html `
                    <p class="it-card-place-address" part="address">
                      <slot name="address" @slotchange=${this.handleSlotChange}></slot>
                    </p>
                  `
                : html `<slot name="address" @slotchange=${this.handleSlotChange}></slot>`}
            </div>
            ${cardImage}
          </div>
          ${cardBody}${cardFooter}${cardActions}
        </article>
      `;
        }
        if (isInline) {
            return html `
        <article class="${classes}" part="card">
          <div class="it-card-inline-content">${cardTitle}${cardBody}${cardFooter}${cardActions}</div>
          ${cardImage}
        </article>
      `;
        }
        return html `
      <article class="${classes}" part="card">${cardTitle}${cardImage}${cardBody}${cardFooter}${cardActions}</article>
    `;
    }
};
ItCard.styles = styles;
__decorate([
    property({ type: Boolean, attribute: 'full-height' }),
    __metadata("design:type", Object)
], ItCard.prototype, "fullHeight", void 0);
__decorate([
    property({ type: String }),
    __metadata("design:type", String)
], ItCard.prototype, "variant", void 0);
__decorate([
    property({ type: String, attribute: 'border-top' }),
    __metadata("design:type", String)
], ItCard.prototype, "borderTop", void 0);
__decorate([
    property({ type: String }),
    __metadata("design:type", String)
], ItCard.prototype, "ratio", void 0);
__decorate([
    property({ type: String }),
    __metadata("design:type", String)
], ItCard.prototype, "shadow", void 0);
__decorate([
    property({ type: String }),
    __metadata("design:type", String)
], ItCard.prototype, "border", void 0);
__decorate([
    property({ type: String, attribute: 'heading-level' }),
    __metadata("design:type", String)
], ItCard.prototype, "headingLevel", void 0);
__decorate([
    property({ type: String, attribute: 'heading-size' }),
    __metadata("design:type", String)
], ItCard.prototype, "headingSize", void 0);
__decorate([
    property({ type: String, attribute: 'it-class' }),
    __metadata("design:type", Object)
], ItCard.prototype, "itClass", void 0);
__decorate([
    property({ type: String, attribute: 'actions-aria-label' }),
    __metadata("design:type", String)
], ItCard.prototype, "actionsAriaLabel", void 0);
__decorate([
    queryAssignedElements({ slot: 'title' }),
    __metadata("design:type", Array)
], ItCard.prototype, "_titleElements", void 0);
__decorate([
    queryAssignedElements({ slot: 'subtitle' }),
    __metadata("design:type", Array)
], ItCard.prototype, "_subtitleElements", void 0);
__decorate([
    queryAssignedElements({ slot: 'address' }),
    __metadata("design:type", Array)
], ItCard.prototype, "_addressElements", void 0);
__decorate([
    queryAssignedElements({ slot: 'signature' }),
    __metadata("design:type", Array)
], ItCard.prototype, "_signatureElements", void 0);
__decorate([
    queryAssignedElements({ slot: 'text' }),
    __metadata("design:type", Array)
], ItCard.prototype, "_textElements", void 0);
__decorate([
    queryAssignedElements({ slot: 'body' }),
    __metadata("design:type", Array)
], ItCard.prototype, "_bodyElements", void 0);
__decorate([
    queryAssignedElements({ slot: 'image' }),
    __metadata("design:type", Array)
], ItCard.prototype, "_imageElements", void 0);
__decorate([
    queryAssignedElements({ slot: 'footer' }),
    __metadata("design:type", Array)
], ItCard.prototype, "_footerElements", void 0);
__decorate([
    queryAssignedElements({ slot: 'actions' }),
    __metadata("design:type", Array)
], ItCard.prototype, "_actionsElements", void 0);
ItCard = __decorate([
    customElement('it-card')
], ItCard);

export { ItCard };
//# sourceMappingURL=it-card.js.map
